import { TagDto } from '@maia/tags';
import { match } from 'ts-pattern';
import {
  AssigneeUpdate,
  NoteUpdate,
  PriorityUpdate,
  StatusUpdate,
  TeamUpdate,
  VisibilityUpdate,
} from '../entitites/task.entity';
import {
  CustomerDto,
  IAttachmentEntity,
  IBrokerEntity,
  ITaskEntity,
  ITeamEntity,
} from '../shared';
import { BrokerDto, undefinedOrBroker } from './broker.dto';
import { undefinedOrTransformToDTOCurried } from '@maia/utils';
import { undefinedOrCustomer } from './customer.dto';
import { SystemDto } from './system.dto';

export class TaskDto {
  readonly guid: string;
  readonly reference: string;
  readonly priority: ReturnType<typeof TagDto.getTaskPriorityTag>;
  readonly status: ReturnType<typeof TagDto.getTaskStatusTag>;
  readonly linkedItem: {
    guid: string;
    type: 'claim' | 'insurance' | 'quotation' | 'customer';
    label: string;
  };
  readonly title: string;
  readonly description: string;
  readonly customer: CustomerDto | undefined;
  readonly noteCount: number;
  readonly leadTime: number;
  readonly createdBy: BrokerDto | SystemDto;
  readonly createdAt: Date;
  readonly estimatedEndDate: Date | undefined;
  readonly assignee: BrokerDto | undefined;
  readonly team: {};
  readonly publiclyVisible: boolean;
  readonly history: Array<
    | ActivityUpdateDto
    | PriorityUpdateDto
    | AssigneeUpdateDto
    | TeamUpdateDto
    | NoteUpdateDto
    | VisibilityUpdateDto
  >;
  readonly attachments:
    | Array<{
        guid: string;
        mimetype: string;
        filename: string;
      }>
    | [];

  constructor(task: ITaskEntity) {
    this.guid = task.guid;
    this.reference = task.reference;
    this.priority = TagDto.getTaskPriorityTag(task.priority ?? 'medium');
    this.linkedItem = {
      guid: task.linked_item.guid ?? '',
      type: task.linked_item.type,
      label: task.linked_item.label ?? '',
    };
    this.status = TagDto.getTaskStatusTag(task.status);
    this.title = task.title ?? '[MISSING TITLE!]';
    this.description = task.description ?? '';
    this.customer = undefinedOrCustomer(task.customer);
    this.noteCount = task.note_count;
    this.leadTime = task.lead_time ?? 1;
    this.createdBy =
      task?.created_by && Object.entries(task?.created_by).length > 0
        ? new BrokerDto(task.created_by)
        : new SystemDto();
    this.createdAt = new Date(task.created_at);
    this.estimatedEndDate = task.estimated_end_date
      ? new Date(task.estimated_end_date)
      : undefined;
    this.assignee = undefinedOrBroker(task.assignee);
    this.team = task.team;
    this.publiclyVisible = task.externally_visible ?? false;
    this.history = task.history.flatMap((item) =>
      match(item)
        .with({ type: 'status' }, (u) => new ActivityUpdateDto(u))
        .with({ type: 'priority' }, (u) => new PriorityUpdateDto(u))
        .with({ type: 'assignee' }, (u) => new AssigneeUpdateDto(u))
        .with({ type: 'team' }, (u) => new TeamUpdateDto(u))
        .with({ type: 'note' }, (u) => new NoteUpdateDto(u))
        .with({ type: 'visibility' }, (u) => new VisibilityUpdateDto(u))
        .otherwise(() => []),
    );
    this.attachments = task.attachments;
  }
}

class ActivityUpdateDto {
  readonly type: 'status';
  readonly createdAt: Date;
  readonly createdBy: BrokerDto;
  readonly status: ReturnType<typeof TagDto.getTaskStatusTag>;

  constructor(update: StatusUpdate) {
    this.type = update.type;
    this.createdAt = new Date(update.created_at);
    this.createdBy = new BrokerDto(update.created_by);
    this.status = TagDto.getTaskStatusTag(update.status);
  }
}

class PriorityUpdateDto {
  readonly type: 'priority';
  readonly createdAt: Date;
  readonly createdBy: BrokerDto;
  readonly priority: ReturnType<typeof TagDto.getTaskPriorityTag>;

  constructor(update: PriorityUpdate) {
    this.type = update.type;
    this.createdAt = new Date(update.created_at);
    this.createdBy = new BrokerDto(update.created_by);
    this.priority = TagDto.getTaskPriorityTag(update.priority);
  }
}

class AssigneeUpdateDto {
  readonly type: 'assignee';
  readonly createdAt: Date;
  readonly createdBy: BrokerDto;
  readonly assignee: BrokerDto | undefined;

  constructor(update: AssigneeUpdate) {
    this.type = update.type;
    this.createdAt = new Date(update.created_at);
    this.createdBy = new BrokerDto(update.created_by);
    this.assignee = undefinedOrBroker(update.assignee);
  }
}

class TeamUpdateDto {
  readonly type: 'team';
  readonly createdAt: Date;
  readonly createdBy: BrokerDto;
  readonly team: string;

  constructor(update: TeamUpdate) {
    this.type = update.type;
    this.createdAt = new Date(update.created_at);
    this.createdBy = new BrokerDto(update.created_by);
    this.team = update.team;
  }
}

class NoteUpdateDto {
  readonly type: 'note';
  readonly createdAt: Date;
  readonly createdBy: BrokerDto;
  readonly content: string;
  readonly attachments?: Array<IAttachmentEntity>;

  constructor(update: NoteUpdate) {
    this.type = update.type;
    this.createdAt = new Date(update.created_at);
    this.createdBy = new BrokerDto(update.created_by);
    this.content = update.content;
    this.attachments = update.attachments;
  }
}

class VisibilityUpdateDto {
  readonly type: 'visibility';
  readonly createdAt: Date;
  readonly createdBy: BrokerDto;
  readonly content: string;

  constructor(update: VisibilityUpdate) {
    this.type = update.type;
    this.createdAt = new Date(update.created_at);
    this.createdBy = new BrokerDto(update.created_by);
    this.content = update.content;
  }
}

export const undefinedOrTask = undefinedOrTransformToDTOCurried(
  (entity: ITaskEntity) => new TaskDto(entity),
);

export class TaskPostMessageDto {
  readonly description: string | undefined;
  readonly attachments?: Array<IAttachmentEntity>;

  constructor(params: {
    body: string;
    attachments?: Array<IAttachmentEntity>;
  }) {
    this.description = params.body;
    this.attachments = params.attachments;
  }
}

export class TaskPutOptionsDto {
  readonly externally_visible: boolean;
  readonly priority: ITaskEntity['status'];
  readonly assignee: IBrokerEntity['guid'] | undefined;
  readonly team: ITeamEntity['guid'] | undefined;
  readonly status: ITaskEntity['status'];

  constructor(params: {
    publiclyVisible: boolean;
    priority: ITaskEntity['status'];
    assignee: string | undefined;
    team: string | undefined;
    status: ITaskEntity['status'];
  }) {
    this.externally_visible = params.publiclyVisible;
    this.priority = params.priority;
    this.assignee = params.assignee;
    this.team = params.team;
    this.status = params.status;
  }
}

export class TaskCreateDto {
  readonly title: string;
  readonly duration: number;
  readonly externally_visible: false;
  readonly description: string;
  readonly attachments: Array<IAttachmentEntity> | undefined;
  readonly linked_item: {
    readonly guid: string;
    readonly type: string;
  };
  readonly assignee?: string;
  readonly team?: string;
  readonly priority: 'low' | 'medium' | 'high' | 'critical';

  constructor(params: {
    title: string;
    duration: number;
    externally_visible: false;
    description: string;
    attachments: Array<IAttachmentEntity> | undefined;
    linked_item: {
      guid: string;
      type: string;
    };
    assignee?: string;
    team?: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
  }) {
    this.title = params.title;
    this.duration = params.duration;
    this.externally_visible = params.externally_visible;
    this.description = params.description;
    this.attachments = params.attachments;
    this.linked_item = params.linked_item;
    this.assignee = params.assignee;
    this.team = params.team;
    this.priority = params.priority;
  }
}
